import { Decimal } from 'decimal.js';
import { currencyConfigFormat } from '@/helpers/formatCurrency';
import { STABLECOIN_MAX_RATE_DISPLAY_DECIMAL_LIMIT } from '@/shared/common/constants/rateDisplayPrecision.constant';
import type { RateBreakdownInput, RateBreakdown } from '@/shared/common/types/rateDetails.type';
import { parseDestinyRateString } from '@/shared/common/utils/parseDestinyRateString.util';
import {
  getStablecoinAwareRateDecimals,
  getStablecoinBreakdownDecimals,
} from '@/shared/common/utils/rateDisplayPrecision.util';

function roundRateHalfUp(value: number, decimalPlaces: number): number {
  return new Decimal(value)
    .toDecimalPlaces(decimalPlaces, Decimal.ROUND_HALF_UP)
    .toNumber();
}

function formatRatePair({
  fromCurrency,
  toCurrency,
  value,
  decimalLimitDefault,
}: {
  fromCurrency: string;
  toCurrency: string;
  value: number;
  decimalLimitDefault: number;
}): string {
  const formattedValue = currencyConfigFormat({
    amount: value,
    currencyCode: toCurrency,
    hasCode: true,
    hasSymbol: false,
    decimalLimitDefault,
  });

  return `1 ${fromCurrency} = ${formattedValue}`;
}

function formatSpreadValue({
  value,
  toCurrency,
  decimalLimitDefault,
}: {
  value: number;
  toCurrency: string;
  decimalLimitDefault: number;
}): string {
  return currencyConfigFormat({
    amount: value,
    currencyCode: toCurrency,
    hasCode: true,
    hasSymbol: false,
    decimalLimitDefault,
  });
}

function toFiniteNumber(value: RateBreakdownInput['rate']): number {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Calcula el desglose de tipo de cambio (Global66 / Spot / Spread) de un movimiento.
 * @param input - Datos crudos del movimiento (amounts, currencies, tasas, fee y `destinyRateString`).
 * @returns `RateBreakdown` con las cadenas listas para UI y la metadata para renderizar cada fila.
 */
export function getRateBreakdown(input: RateBreakdownInput): RateBreakdown {
  const originCurrency = input.originCurrency ?? '';
  const destinationCurrency = input.destinationCurrency ?? '';
  const fee = toFiniteNumber(input.fee);
  const rate = toFiniteNumber(input.rate);
  const destinyRate = toFiniteNumber(input.destinyRate);
  const originAmount = toFiniteNumber(input.originAmount);
  const destinationAmount = toFiniteNumber(input.destinationAmount);

  if (originCurrency && destinationCurrency && originCurrency === destinationCurrency) {
    return {
      showBreakdown: false,
      spotFormatted: '',
      spreadFormatted: '',
      global66Formatted: '',
      global66Fallback: false,
    };
  }

  const parsed = parseDestinyRateString(input.destinyRateString);

  if (!parsed) {
    let global66Formatted = '';

    if (originAmount > 0 && destinationAmount > 0) {
      // Decimal en vez de division nativa: el cociente en punto flotante trae
      // ruido que getStablecoinAwareRateDecimals contaria como precision real
      // (1000/333.33 renderizaba 0.33332999999).
      const effectiveRate = new Decimal(destinationAmount)
        .dividedBy(originAmount)
        .toDecimalPlaces(
          STABLECOIN_MAX_RATE_DISPLAY_DECIMAL_LIMIT,
          Decimal.ROUND_HALF_UP
        )
        .toNumber();
      global66Formatted = formatRatePair({
        fromCurrency: originCurrency,
        toCurrency: destinationCurrency,
        value: effectiveRate,
        decimalLimitDefault: getStablecoinAwareRateDecimals(
          effectiveRate,
          originCurrency,
          destinationCurrency
        ),
      });
    }

    return {
      showBreakdown: false,
      spotFormatted: '',
      spreadFormatted: '',
      global66Formatted,
      global66Fallback: true,
    };
  }

  const isMatchForward =
    parsed.fromCurrency === originCurrency && parsed.toCurrency === destinationCurrency;
  const isMatchReverse =
    parsed.fromCurrency === destinationCurrency && parsed.toCurrency === originCurrency;
  // Decimales del global66 del backend (parsed.toValue), para los fallbacks de
  // una sola fila, donde no hay spread que revelar.
  const parsedToValueDecimalLimit = getStablecoinAwareRateDecimals(
    parsed.toValue,
    parsed.fromCurrency,
    parsed.toCurrency
  );

  if (!isMatchForward && !isMatchReverse) {
    return {
      showBreakdown: false,
      spotFormatted: '',
      spreadFormatted: '',
      global66Formatted: formatRatePair({
        fromCurrency: parsed.fromCurrency,
        toCurrency: parsed.toCurrency,
        value: parsed.toValue,
        decimalLimitDefault: parsedToValueDecimalLimit,
      }),
      global66Fallback: false,
    };
  }

  if (rate <= 0 || destinyRate <= 0) {
    return {
      showBreakdown: false,
      spotFormatted: '',
      spreadFormatted: '',
      global66Formatted: formatRatePair({
        fromCurrency: parsed.fromCurrency,
        toCurrency: parsed.toCurrency,
        value: parsed.toValue,
        decimalLimitDefault: parsedToValueDecimalLimit,
      }),
      global66Fallback: false,
    };
  }

  if (fee <= 0) {
    return {
      showBreakdown: false,
      spotFormatted: '',
      spreadFormatted: '',
      global66Formatted: formatRatePair({
        fromCurrency: parsed.fromCurrency,
        toCurrency: parsed.toCurrency,
        value: parsed.toValue,
        decimalLimitDefault: parsedToValueDecimalLimit,
      }),
      global66Fallback: false,
    };
  }

  const spotRawValue = isMatchForward ? destinyRate / rate : rate / destinyRate;
  // Los decimales salen de los campos verbatim del backend (el global66 del
  // string y las dos tasas del spot), nunca de spotRawValue, que es la division
  // en punto flotante entre ellas.
  const decimals = getStablecoinBreakdownDecimals({
    backendRateValues: [parsed.toValue, destinyRate, rate],
    rawSpotRateValue: spotRawValue,
    rawGlobal66RateValue: parsed.toValue,
    originCurrency: parsed.fromCurrency,
    destinationCurrency: parsed.toCurrency,
  });
  const spotValue = roundRateHalfUp(spotRawValue, decimals);
  const spreadValue = roundRateHalfUp(
    new Decimal(spotValue).minus(parsed.toValue).abs().toNumber(),
    decimals
  );

  return {
    showBreakdown: true,
    spotFormatted: formatRatePair({
      fromCurrency: parsed.fromCurrency,
      toCurrency: parsed.toCurrency,
      value: spotValue,
      decimalLimitDefault: decimals,
    }),
    spreadFormatted: formatSpreadValue({
      value: spreadValue,
      toCurrency: parsed.toCurrency,
      decimalLimitDefault: decimals,
    }),
    global66Formatted: formatRatePair({
      fromCurrency: parsed.fromCurrency,
      toCurrency: parsed.toCurrency,
      value: parsed.toValue,
      decimalLimitDefault: decimals,
    }),
    global66Fallback: false,
  };
}
